import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackLeadCreated } from "@/lib/oaiq";

interface FormField {
  id: string;
  field_type: string;
  label: string;
  placeholder: string | null;
  help_text: string | null;
  required: boolean;
  options: string[];
  sort_order: number;
  step: number;
}

interface FormData {
  id: string;
  title: string;
  description: string | null;
  thank_you_title: string | null;
  thank_you_message: string | null;
}

export default function FormRenderer() {
  const { slug } = useParams();
  const [form, setForm] = useState<FormData | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: formData } = await supabase
        .from("forms")
        .select("id, title, description, thank_you_title, thank_you_message")
        .eq("slug", slug)
        .eq("status", "published")
        .single();

      if (!formData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setForm(formData);

      const { data: fieldsData } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formData.id)
        .order("sort_order", { ascending: true });

      const parsed = (fieldsData || []).map((f) => ({
        ...f,
        options: Array.isArray(f.options) ? (f.options as string[]) : [],
      }));
      setFields(parsed);
      setLoading(false);
    };
    load();
  }, [slug]);

  const totalSteps = fields.length > 0 ? Math.max(...fields.map((f) => f.step)) : 1;
  const currentFields = fields.filter((f) => f.step === currentStep);
  const progress = (currentStep / totalSteps) * 100;

  const validateStep = useCallback(() => {
    const newErrors: Record<string, string> = {};
    currentFields.forEach((field) => {
      const val = values[field.id] || "";
      if (field.required && !val.trim()) {
        newErrors[field.id] = "This field is required";
      }
      if (field.field_type === "email" && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[field.id] = "Please enter a valid email";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentFields, values]);

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < totalSteps) {
      setCurrentStep((s) => s + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!form) return;
    setSubmitting(true);

    // Build data with labels as keys
    const data: Record<string, string> = {};
    fields.forEach((f) => {
      if (values[f.id]) data[f.label] = values[f.id];
    });

    const { error } = await supabase.from("form_submissions").insert({
      form_id: form.id,
      data_json: data,
      source_url: window.location.href,
    });

    setSubmitting(false);
    if (error) {
      toast({ title: "Submission failed", description: error.message, variant: "destructive" });
    } else {
      setSubmitted(true);
      trackLeadCreated();
      // Fire-and-forget notification (Slack, Email, Telegram)
      supabase.functions.invoke("notify-form-submission", {
        body: { submission_data: data, form_title: form.title },
      }).catch((err) => console.warn("Notification failed:", err));
    }
  };

  const setValue = (fieldId: string, value: string) => {
    setValues((v) => ({ ...v, [fieldId]: value }));
    setErrors((e) => { const n = { ...e }; delete n[fieldId]; return n; });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Form not found</h1>
          <p className="text-muted-foreground mt-2">This form doesn't exist or is no longer available.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md space-y-4 animate-in fade-in-0 zoom-in-95 duration-500">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">{form?.thank_you_title || "Thank you!"}</h1>
          <p className="text-muted-foreground text-lg">{form?.thank_you_message || "Your response has been recorded."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress */}
      {totalSteps > 1 && (
        <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b border-border px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-medium">
              Step {currentStep} of {totalSteps}
            </span>
            <Progress value={progress} className="flex-1 h-1.5" />
          </div>
        </div>
      )}

      {/* Form content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl space-y-8 animate-in fade-in-0 slide-in-from-right-4 duration-300" key={currentStep}>
          {/* Form title (only on step 1) */}
          {currentStep === 1 && (
            <div className="space-y-2">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">{form?.title}</h1>
              {form?.description && (
                <p className="text-lg text-muted-foreground">{form.description}</p>
              )}
            </div>
          )}

          {/* Fields */}
          <div className="space-y-8">
            {currentFields.map((field) => (
              <div key={field.id} className="space-y-3">
                <div>
                  <Label className="text-base font-medium">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                  {field.help_text && (
                    <p className="text-sm text-muted-foreground mt-1">{field.help_text}</p>
                  )}
                </div>

                {renderField(field, values[field.id] || "", (v) => setValue(field.id, v))}

                {errors[field.id] && (
                  <p className="text-sm text-destructive">{errors[field.id]}</p>
                )}
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4">
            <div>
              {currentStep > 1 && (
                <Button variant="ghost" onClick={handleBack} className="gap-2">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
            </div>
            <Button
              onClick={handleNext}
              disabled={submitting}
              className="gap-2 min-w-[140px]"
              size="lg"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : currentStep === totalSteps ? (
                <>Submit <Check className="h-4 w-4" /></>
              ) : (
                <>Next <ArrowRight className="h-4 w-4" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderField(
  field: FormField,
  value: string,
  onChange: (v: string) => void,
) {
  switch (field.field_type) {
    case "text":
    case "phone":
    case "number":
    case "url":
      return (
        <Input
          type={field.field_type === "number" ? "number" : field.field_type === "url" ? "url" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className="h-12 text-base"
        />
      );

    case "email":
      return (
        <Input
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || "you@example.com"}
          className="h-12 text-base"
        />
      );

    case "long_text":
      return (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          rows={4}
          className="text-base"
        />
      );

    case "dropdown":
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-12 text-base">
            <SelectValue placeholder={field.placeholder || "Select an option..."} />
          </SelectTrigger>
          <SelectContent>
            {field.options.filter(Boolean).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "multiple_choice":
      return (
        <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
          {field.options.filter(Boolean).map((opt) => (
            <label
              key={opt}
              className={cn(
                "flex items-center gap-3 p-4 rounded-lg border border-border cursor-pointer transition-colors",
                value === opt ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              )}
            >
              <RadioGroupItem value={opt} />
              <span className="text-sm font-medium">{opt}</span>
            </label>
          ))}
        </RadioGroup>
      );

    case "yes_no":
      return (
        <div className="flex gap-3">
          {["Yes", "No"].map((opt) => (
            <Button
              key={opt}
              type="button"
              variant={value === opt ? "default" : "outline"}
              className="flex-1 h-12 text-base"
              onClick={() => onChange(opt)}
            >
              {opt}
            </Button>
          ))}
        </div>
      );

    default:
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className="h-12 text-base"
        />
      );
  }
}
